import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material'
import { CalendarDotIcon, CaretDownIcon, CaretRightIcon, ChatsCircleIcon, PaperclipIcon } from '@phosphor-icons/react'
import {
    ConfirmationDialog,
    LoadingSkeleton,
    OptionsMenu,
    TaskTypeLabelWithIcon,
    TaskVisibilityIcon,
    TextWithEllipsis,
    tokens,
    User,
} from 'ComponentLibrary'
import { Toggle } from 'ComponentLibrary/src/atoms/Toggle/Toggle'
import { Task } from 'ComponentLibrary/src/common/tasks'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { DateWithIcon } from 'ComponentLibrary/src/molecules/DateWithIcon/DateWithIcon'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TaskAssigneePopover } from 'ComponentLibrary/src/widgets/TaskListWithDashboard/TaskAssigneePopover'
import { isEmpty, partial } from 'lodash'
import { ReactElement, ReactNode, useState } from 'react'
import invariant from 'tiny-invariant'
import { SizingSpacing100 } from 'ComponentLibrary/style-dictionary/output/tokens'
import { TaskAssignee } from 'ComponentLibrary/src/common/users'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type TaskListWithDashboardProps = {
    tasksInOpen: DashboardTask[]
    tasksInDone: DashboardTask[]
    onTaskClick: (taskId: UUID) => void
    toggleTaskStatus: (task: Pick<DashboardTask, 'status' | 'uuid'>) => void
    assigneeOptions: User[]
    onChangeAssignee: (taskId: UUID, assignee: TaskAssignee | null) => void
    filtersSlot?: ReactNode
    showTaskVisibility: boolean
    isLoading?: boolean
    deleteTaskProps:
        | {
              deleteTask: DeleteTaskCallback
              isDeletePending: boolean
          }
        | undefined
    copy?: Partial<TaskListWithDashboardCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

type DeleteTaskCallback = (variables: { taskId: UUID }) => void

export type DashboardTask = Pick<
    Task,
    'uuid' | 'title' | 'description' | 'due_date' | 'status' | 'has_limited_visibility' | 'task_type'
> & {
    threadsCount?: number
    attachmentsCount?: number
    assignee: TaskAssignee | undefined
}

export type TaskListWithDashboardCopy = {
    open: string
    completed: string
    noDeadline: string
    deleteTask: string
    deleteConfirm: string
    deleteTitle: (taskTitle: string) => string
    assigneeSearchPlaceholder: string
}

const DEFAULT_TASK_LIST_WITH_DASHBOARD_COPY: TaskListWithDashboardCopy = {
    open: 'Offen',
    completed: 'Abgeschlossen',
    noDeadline: 'keine Deadline',
    deleteTask: 'Aufgabe löschen',
    deleteConfirm: 'löschen',
    deleteTitle: (taskTitle) => `Aufgabe "${taskTitle}" wirklich löschen?`,
    assigneeSearchPlaceholder: 'Nutzer durchsuchen',
}

export function TaskListWithDashboard({
    tasksInDone,
    tasksInOpen,
    onTaskClick,
    toggleTaskStatus,
    assigneeOptions,
    onChangeAssignee,
    filtersSlot,
    showTaskVisibility,
    isLoading,
    deleteTaskProps,
    copy,
    userLinkCopy,
}: TaskListWithDashboardProps) {
    const resolvedCopy = { ...DEFAULT_TASK_LIST_WITH_DASHBOARD_COPY, ...copy }
    return (
        <Stack direction="column" gap={tokens.SizingSpacing500}>
            {filtersSlot}
            <TaskListAccordion
                hideExpandIcon={isEmpty(tasksInOpen)}
                title={getAccordionTitle(resolvedCopy.open, tasksInOpen.length)}
                defaultExpanded
            >
                {isLoading ? (
                    <LoadingSkeleton count={4} height="58px" sx={{ transform: 'none', borderRadius: tokens.SizingCornerL }} />
                ) : (
                    tasksInOpen.map((task) => (
                        <TaskListItem
                            showTaskVisibility={showTaskVisibility && task.task_type !== 'system'}
                            key={task.uuid}
                            task={task}
                            onClick={() => onTaskClick(task.uuid)}
                            toggleStatus={() => toggleTaskStatus(task)}
                            assigneeOptions={assigneeOptions}
                            onChangeAssignee={partial(onChangeAssignee, task.uuid)}
                            deleteTaskProps={deleteTaskProps}
                            copy={resolvedCopy}
                            userLinkCopy={userLinkCopy}
                        />
                    ))
                )}
            </TaskListAccordion>
            <TaskListAccordion hideExpandIcon={isEmpty(tasksInDone)} title={getAccordionTitle(resolvedCopy.completed, tasksInDone.length)}>
                {isLoading ? (
                    <LoadingSkeleton count={4} height="58px" sx={{ transform: 'none', borderRadius: tokens.SizingCornerL }} />
                ) : (
                    tasksInDone.map((task) => (
                        <TaskListItem
                            showTaskVisibility={showTaskVisibility}
                            key={task.uuid}
                            task={task}
                            onClick={() => onTaskClick(task.uuid)}
                            toggleStatus={() => toggleTaskStatus(task)}
                            assigneeOptions={assigneeOptions}
                            onChangeAssignee={partial(onChangeAssignee, task.uuid)}
                            deleteTaskProps={deleteTaskProps}
                            copy={resolvedCopy}
                            userLinkCopy={userLinkCopy}
                        />
                    ))
                )}
            </TaskListAccordion>
        </Stack>
    )
}

const getAccordionTitle = (title: string, count: number) => `${title} (${count})`

type TaskListAccordionProps = { title: string; children: ReactNode; defaultExpanded?: boolean; hideExpandIcon?: boolean }

function TaskListAccordion({ title, children, defaultExpanded, hideExpandIcon }: TaskListAccordionProps) {
    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                '&:before': {
                    display: 'none',
                },
                pointerEvents: hideExpandIcon ? 'none' : 'auto',
            }}
            defaultExpanded={defaultExpanded}
        >
            <AccordionSummary
                expandIcon={
                    hideExpandIcon ? null : <CaretDownIcon weight="bold" color={tokens.ColorIconDefault} size={tokens.SizingIconM} />
                }
                sx={{ paddingBottom: tokens.SizingSpacing150 }}
            >
                <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: tokens.SizingSpacing150 }}>{children}</AccordionDetails>
        </Accordion>
    )
}

type TaskListItemProps = {
    task: DashboardTask
    onClick: () => void
    toggleStatus: () => void
    onChangeAssignee: (assignee: TaskAssignee | null) => void
    showTaskVisibility: boolean
    deleteTaskProps: TaskListWithDashboardProps['deleteTaskProps']
    copy: TaskListWithDashboardCopy
    userLinkCopy?: Partial<UserLinkCopy>
} & Pick<TaskListWithDashboardProps, 'assigneeOptions'>

export function TaskListItem({
    task,
    onClick,
    toggleStatus,
    assigneeOptions,
    onChangeAssignee,
    showTaskVisibility,
    deleteTaskProps,
    copy,
    userLinkCopy,
}: TaskListItemProps) {
    const isDone = task.status === 'done'
    const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false)

    return (
        <>
            <Stack
                direction="row"
                gap={tokens.SizingSpacing150}
                onClick={onClick}
                alignItems="center"
                padding={`${SizingSpacing100} ${tokens.SizingSpacing200}`}
                borderRadius={tokens.SizingCornerL}
                border={`1px solid ${tokens.ColorBorderDefault}`}
                height="58px"
                sx={{
                    background: tokens.ColorSurfaceDefault,
                    ':hover': {
                        background: tokens.ColorSurfaceHover,
                        cursor: 'pointer',
                    },
                }}
            >
                <Toggle
                    isOn={isDone}
                    onToggle={(event) => {
                        event.stopPropagation()
                        toggleStatus()
                    }}
                />
                <Stack direction="column" width="60%">
                    <TextWithEllipsis
                        tooltipText={task.title}
                        typographyProps={{ variant: 'headline3', color: isDone ? tokens.ColorTextSubtle : tokens.ColorTextBoldest }}
                    >
                        {isDone ? <del>{task.title}</del> : task.title}
                    </TextWithEllipsis>
                    <TextWithEllipsis
                        tooltipText={task.description}
                        typographyProps={{
                            variant: 'bodyTextMedium',
                            color: isDone ? tokens.ColorTextDisabled : tokens.ColorTextDefault,
                        }}
                    >
                        {task.description}
                    </TextWithEllipsis>
                </Stack>
                <Stack marginLeft="auto" direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    {task.attachmentsCount !== undefined && task.attachmentsCount > 0 && (
                        <IconWithLabel
                            icon={<PaperclipIcon color={tokens.ColorIconDefault} size={tokens.SizingIconS} weight="bold" />}
                            label={task.attachmentsCount}
                        />
                    )}
                    {task.threadsCount !== undefined && task.threadsCount > 0 && (
                        <IconWithLabel
                            icon={<ChatsCircleIcon color={tokens.ColorIconDefault} size={tokens.SizingIconS} weight="bold" />}
                            label={task.threadsCount}
                        />
                    )}
                    {showTaskVisibility && <TaskVisibilityIcon isLimitedVisibility={!!task.has_limited_visibility} size="small" />}
                    <TaskTypeLabelWithIcon
                        taskType="todo"
                        iconColor={isDone ? tokens.ColorIconDisabled : undefined}
                        typographyProps={{
                            color: isDone ? tokens.ColorTextDisabled : undefined,
                            variant: 'textS',
                        }}
                    />
                    <DateWithIcon
                        CalendarIconComponent={CalendarDotIcon}
                        date={task.due_date}
                        noDateText={copy.noDeadline}
                        typographyProps={{
                            color: isDone ? tokens.ColorTextDisabled : tokens.ColorTextDefault,
                            variant: 'textS',
                        }}
                        iconColor={isDone ? tokens.ColorIconDisabled : tokens.ColorIconDefault}
                    />
                    <TaskAssigneePopover
                        assignee={task.assignee || null}
                        options={assigneeOptions}
                        onChange={onChangeAssignee}
                        copy={{ searchPlaceholder: copy.assigneeSearchPlaceholder }}
                        userLinkCopy={userLinkCopy}
                    />
                    {deleteTaskProps ? (
                        <OptionsMenu
                            options={[
                                {
                                    title: copy.deleteTask,
                                    callback: () => setIsDeleteConfirmationDialogOpen(true),
                                    danger: true,
                                },
                            ]}
                        />
                    ) : (
                        <CaretRightIcon weight="bold" size={tokens.SizingIconM} />
                    )}
                </Stack>
            </Stack>
            <ConfirmationDialog
                open={isDeleteConfirmationDialogOpen}
                onConfirm={() => {
                    invariant(deleteTaskProps !== undefined, 'delete task props cannot be undefined')
                    deleteTaskProps.deleteTask({ taskId: task.uuid })
                }}
                isPending={deleteTaskProps?.isDeletePending}
                onCancel={() => setIsDeleteConfirmationDialogOpen(false)}
                destructive
                confirmLabel={copy.deleteConfirm}
                title={copy.deleteTitle(task.title)}
            />
        </>
    )
}

function IconWithLabel({ icon, label }: { icon: ReactElement; label: string | number }) {
    return (
        <Stack direction="row" gap={tokens.SizingSpacing050}>
            {icon}
            <Typography variant="textS" color={tokens.ColorTextDefault}>
                {label}
            </Typography>
        </Stack>
    )
}
